import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InfoTooltip from '../../../components/ui/Tooltip';

const ProjectionTable = ({ data, projectionYears = 20 }) => {
  const [expandedYears, setExpandedYears] = useState([1, 5, 10]);

  const formatCurrency = (value) => {
    // Handle NaN, null, undefined, or invalid values
    const numValue = parseFloat(value);
    if (isNaN(numValue) || numValue === null || numValue === undefined) {
      return '$0';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(numValue);
  };

  const formatPercent = (value) => {
    return `${value?.toFixed(2)}%`;
  };

  const toggleYear = (year) => {
    setExpandedYears(prev =>
      prev?.includes(year)
        ? prev?.filter(y => y !== year)
        : [...prev, year]
    );
  };

  const toggleAll = () => {
    if (expandedYears?.length === data?.length) {
      setExpandedYears([]);
    } else {
      setExpandedYears(data?.map(d => d?.year));
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-2">
            Projections annuelles détaillées
          </h2>
          <p className="text-sm text-muted-foreground">
            Analyse année par année des revenus, dépenses et rendements
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName={expandedYears?.length === data?.length ? 'ChevronsUp' : 'ChevronsDown'}
          iconPosition="left"
          onClick={toggleAll}
          className="w-full md:w-auto"
        >
          {expandedYears?.length === data?.length ? 'Tout réduire' : 'Tout développer'}
        </Button>
      </div>
      <div className="space-y-2">
        {data?.map((yearData) => (
          <div
            key={yearData?.year}
            className="border border-border rounded-lg transition-base relative"
          >
            <button
              onClick={() => toggleYear(yearData?.year)}
              className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-base rounded-t-lg"
            >
              <div className="flex items-center gap-3">
                <Icon
                  name={expandedYears?.includes(yearData?.year) ? 'ChevronDown' : 'ChevronRight'}
                  size={20}
                  className="text-muted-foreground"
                />
                <span className="font-semibold text-foreground">Année {yearData?.year}</span>
              </div>
              <div className="flex items-center gap-4 md:gap-8">
                <div className="text-right">
                  <p className="caption text-muted-foreground">Cashflow</p>
                  <p className="font-semibold text-foreground data-text">
                    {formatCurrency(yearData?.annualCashflow)}
                  </p>
                </div>
                <div className="text-right hidden md:block">
                  <div className="flex items-center justify-end gap-1">
                    <p className="caption text-muted-foreground">ROI</p>
                    <InfoTooltip content="Retour sur investissement cumulatif incluant le cashflow et l'équité gagnée." />
                  </div>
                  <p className="font-semibold text-success data-text">
                    {formatPercent(yearData?.cumulativeROI)}
                  </p>
                </div>
              </div>
            </button>

            {expandedYears?.includes(yearData?.year) && (
              <div className="border-t border-border p-4 bg-muted/20 rounded-b-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <h4 className="caption font-semibold text-foreground mb-3">Revenus</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Revenus locatifs</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.rentalIncome)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Autres revenus</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.otherIncome ?? 0)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-border">
                        <span className="caption font-semibold text-foreground">Total revenus</span>
                        <span className="caption font-semibold data-text">{formatCurrency(yearData?.totalIncome)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="caption font-semibold text-foreground mb-3">Dépenses</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Taxes foncières</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.propertyTaxes ?? 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Assurances</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.insurance ?? 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Entretien</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.maintenance ?? 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Gestion</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.management ?? 0)}</span>
                      </div>
                      <div className="flex justify-between pt-2 border-t border-border">
                        <span className="caption font-semibold text-foreground">Total dépenses</span>
                        <span className="caption font-semibold data-text">{formatCurrency(yearData?.totalExpenses)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="caption font-semibold text-foreground mb-3">Rendements</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1">
                          <span className="caption text-muted-foreground">NOI</span>
                          <InfoTooltip content="Le Revenu Net Opérationnel (NOI) est le revenu total après toutes les dépenses d'opération, mais avant l'hypothèque." />
                        </div>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.noi)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Paiement hypothécaire</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.mortgagePayment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Cashflow mensuel</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.monthlyCashflow)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="caption text-muted-foreground">Valeur propriété</span>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.propertyValue)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-1">
                          <span className="caption text-muted-foreground">Équité</span>
                          <InfoTooltip content="La différence entre la valeur marchande estimée de l'immeuble et le solde de l'hypothèque." />
                        </div>
                        <span className="caption font-medium data-text">{formatCurrency(yearData?.equity)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProjectionTable;